import React, { useState, useEffect } from "react";
import { Table, Button, message, Modal, Select, Popconfirm, Card, Tag } from "antd";
import { DeleteOutlined, EditOutlined, UserOutlined, TeamOutlined } from "@ant-design/icons";
import api from "../utils/api";

const { Option } = Select;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/users");
      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        message.error(response.data.message || "获取用户列表失败");
      }
    } catch (error) {
      console.error("Fetch users error:", error);
      message.error(error.response?.data?.message || "获取用户列表失败");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      if (response.data.success) {
        message.success(`用户 ${username} 已删除`);
        fetchUsers(); // Refresh user list
      } else {
        message.error(response.data.message || "删除用户失败");
      }
    } catch (error) {
      console.error("Delete user error:", error);
      message.error(error.response?.data?.message || "删除用户失败");
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;

    try {
      const response = await api.put(`/admin/users/${selectedUser.id}/role`, { role: newRole });
      if (response.data.success) {
        message.success(`用户 ${selectedUser.username} 角色已更新为 ${newRole === "admin" ? "管理员" : "普通用户"}`);
        setRoleModalVisible(false);
        setSelectedUser(null);
        setNewRole("");
        fetchUsers(); // Refresh user list
      } else {
        message.error(response.data.message || "更新角色失败");
      }
    } catch (error) {
      console.error("Update role error:", error);
      message.error(error.response?.data?.message || "更新角色失败");
    }
  };

  const showRoleModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleModalVisible(true);
  };

  const columns = [
    {
      title: "用户名",
      dataIndex: "username",
      key: "username",
      render: (text, record) => (
        <span>
          {record.role === "admin" ? (
            <TeamOutlined style={{ marginRight: 8 }} />
          ) : (
            <UserOutlined style={{ marginRight: 8 }} />
          )}
          {text}
        </span>
      ),
    },
    {
      title: "角色",
      dataIndex: "role",
      key: "role",
      render: (text) => (
        <Tag color={text === "admin" ? "red" : "blue"}>
          {text === "admin" ? "管理员" : "普通用户"}
        </Tag>
      ),
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: "操作",
      key: "action",
      render: (_, record) => (
        <div>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showRoleModal(record)}
            style={{ marginRight: 8 }}
          >
            修改角色
          </Button>
          <Popconfirm
            title={`确定要删除用户 ${record.username} 吗？`}
            onConfirm={() => handleDeleteUser(record.id, record.username)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Card
      title="用户管理"
      style={{
        margin: "20px",
        maxWidth: "1000px",
        marginLeft: "auto",
        marginRight: "auto",
      }}
    >
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
        }}
      />

      {/* Role Update Modal */}
      <Modal
        title={`修改用户角色 - ${selectedUser?.username}`}
        open={roleModalVisible}
        onCancel={() => {
          setRoleModalVisible(false);
          setSelectedUser(null);
          setNewRole("");
        }}
        onOk={handleUpdateRole}
        okText="确定"
        cancelText="取消"
      >
        <div style={{ padding: "20px 0" }}>
          <p style={{ marginBottom: 16 }}>选择新角色：</p>
          <Select
            value={newRole}
            onChange={setNewRole}
            style={{ width: "100%" }}
            placeholder="选择角色"
          >
            <Option value="user">普通用户</Option>
            <Option value="admin">管理员</Option>
          </Select>
        </div>
      </Modal>
    </Card>
  );
};

export default UserManagement;